import React, { useState, useEffect } from "react";
import CreatableSelect from "react-select/creatable";
import {
  createTask,
  EditTasks,
  getAdmins,
  getStudents,
} from "../../../services/admin.services";
import { fetchTasks } from "../../../state/reducers/dashboardSlice";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

const AddTaskModal = ({ isOpen, onClose }) => {
  const task = useSelector((state) => state.dashboard.selectedTask);
  const dispatch = useDispatch();

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startDate: "",
    relatedTo: "",
    relatedToType: "Student",
    priority: "Low",
    tags: [],
  });

  // Other state
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [admins, setAdmins] = useState([]);

  // Set default start date on initial render
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setFormData((prev) => ({ ...prev, startDate: today }));
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle radio button change for related to type
  const handleRelatedToTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      relatedToType: type,
      relatedTo: "", // Reset selection when type changes
    }));
  };

  // Handle tag changes
  const handleTagsChange = (newTags) => {
    setFormData((prev) => ({ ...prev, tags: newTags || [] }));
  };

  // Handle priority selection
  const handlePriorityChange = (level) => {
    setFormData((prev) => ({ ...prev, priority: level }));
  };

  // Fetch users data when modal opens
  const fetchData = async () => {
    if (!isOpen) return;

    setLoading(true);
    try {
      // Make requests in parallel
      const [studentRes, adminRes] = await Promise.all([
        getStudents(),
        getAdmins(),
      ]);

      setStudents(
        studentRes.data.data.map((student) => ({
          id: student.student_id,
          name: student.username,
        }))
      );

      setAdmins(
        adminRes.data.map((admin) => ({
          id: admin.admin_id,
          name: admin.username || admin.name,
        }))
      );
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users data");
    } finally {
      setLoading(false);
    }
  };

  // Load data on modal open
  useEffect(() => {
    if (isOpen) {
      fetchData();

      // Reset form when opening for new task
      if (!task) {
        setFormData({
          title: "",
          description: "",
          startDate: new Date().toISOString().split("T")[0],
          relatedTo: "",
          relatedToType: "Student",
          priority: "Low",
          tags: [],
        });
      }
    }
  }, [isOpen]);

  // Populate form with task data when editing
  useEffect(() => {
    if (task && isOpen) {
      setFormData({
        title: task.title || "",
        description: task.description || "",
        startDate: task.start_date || "",
        relatedTo: task.student_id || task.admin_id || "",
        relatedToType: task.student_id ? "Student" : "Admin",
        priority: task.priority || "Low",
        tags: task.tags?.map((tag) => ({ label: tag, value: tag })) || [],
      });
    }
  }, [task, isOpen]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Form validation
    if (!formData.title.trim()) {
      toast.error("Task title is required");
      return;
    }

    if (!formData.relatedTo) {
      toast.error(`Please select a ${formData.relatedToType.toLowerCase()}`);
      return;
    }

    setLoading(true);

    try {
      const taskData = {
        title: formData.title,
        description: formData.description,
        start_date: formData.startDate,
        related_to: formData.relatedTo,
        priority: formData.priority,
        tags: formData.tags.map((tag) => tag.value),
      };

      if (task) {
        // Editing existing task
        await EditTasks(task.id, { ...taskData, id: task.id });
        toast.success("Task updated successfully");
      } else {
        // Creating new task
        await createTask(taskData);
        toast.success("Task created successfully");
      }

      // Refresh task list
      await dispatch(fetchTasks());
      onClose();
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error(task ? "Failed to update task" : "Failed to create task");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-5 rounded-t-xl">
          <h2 className="text-xl font-semibold">
            {task ? "Edit Task" : "Create New Task"}
          </h2>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white opacity-80 hover:opacity-100"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Task Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task Title*
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter task title"
                required
              />
            </div>

            {/* Task Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description*
              </label>
              <textarea
                rows="3"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Describe the task"
                required
              ></textarea>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date*
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            {/* Related To Selection */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Assign To*
              </label>

              {/* Radio toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
                <button
                  type="button"
                  onClick={() => handleRelatedToTypeChange("Student")}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    formData.relatedToType === "Student"
                      ? "bg-white shadow-sm text-purple-700"
                      : "text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Student
                </button>
                <button
                  type="button"
                  onClick={() => handleRelatedToTypeChange("Admin")}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    formData.relatedToType === "Admin"
                      ? "bg-white shadow-sm text-purple-700"
                      : "text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Admin
                </button>
              </div>

              {/* Dropdown */}
              <select
                name="relatedTo"
                value={formData.relatedTo}
                onChange={handleChange}
                className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              >
                <option value="">
                  Select a {formData.relatedToType.toLowerCase()}
                </option>
                {(formData.relatedToType === "Student" ? students : admins).map(
                  (person) => (
                    <option key={person.id} value={person.id}>
                      {person.name}
                    </option>
                  )
                )}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <div className="grid grid-cols-3 gap-2">
                {["Low", "Medium", "High"].map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => handlePriorityChange(level)}
                    className={`py-2 rounded-lg transition-colors ${
                      formData.priority === level
                        ? level === "Low"
                          ? "bg-green-100 text-green-800 border border-green-300"
                          : level === "Medium"
                          ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                          : "bg-red-100 text-red-800 border border-red-300"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <CreatableSelect
                isMulti
                value={formData.tags}
                onChange={handleTagsChange}
                placeholder="Type and press Enter to add tags"
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#D1D5DB",
                    padding: "4px",
                    "&:hover": {
                      borderColor: "#9CA3AF",
                    },
                  }),
                }}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:from-purple-700 hover:to-indigo-700 font-medium"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {task ? "Updating..." : "Creating..."}
                </div>
              ) : (
                <>{task ? "Update Task" : "Create Task"}</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTaskModal;
